import React, { useRef, useState, useEffect, useCallback } from "react";
import { aiShowcaseItems } from "../data/aiShowcase";
import { Reveal } from "./Reveal";
import { ImageOff, PlayCircle, Clapperboard, Play, X } from "lucide-react";

const toEmbed = (u) => {
  if (!u) return "";
  try {
    const url = new URL(u);
    const host = url.hostname.replace("www.", "");
    if (host.includes("youtu.be")) return `https://www.youtube.com/embed/${url.pathname.slice(1)}?autoplay=1`;
    if (host.includes("youtube.com")) return `https://www.youtube.com/embed/${url.searchParams.get("v")}?autoplay=1`;
    if (host.includes("vimeo.com")) return `https://player.vimeo.com/video/${url.pathname.split("/").filter(Boolean).pop()}?autoplay=1`;
    return u;
  } catch {
    return u;
  }
};

const Placeholder = ({ icon, label }) => (
  <div className="flex h-72 w-full flex-col items-center justify-center gap-3 bg-[var(--elevated)] text-[var(--muted)]">
    <span className="text-gold">{icon}</span>
    <span className="text-xs uppercase tracking-[0.25em]">{label} — replace me</span>
  </div>
);

const Caption = ({ title, sub }) => (
  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-5">
    <p className="text-sm text-[var(--text)]">{title}</p>
    <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{sub}</p>
  </div>
);

const ImageCard = ({ item, onOpen }) => (
  <div data-testid="ai-item-image" onClick={onOpen}
    className="card group relative cursor-pointer overflow-hidden">
    {item.src ? (
      <img src={item.src} alt={item.title} className="h-72 w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
    ) : <Placeholder icon={<ImageOff size={28} strokeWidth={1.4} />} label="AI image" />}
    <Caption title={item.title} sub={item.caption || "AI Image"} />
  </div>
);

const VideoCard = ({ item, onOpen }) => {
  const ref = useRef(null);
  return (
    <div data-testid="ai-item-video" onClick={onOpen}
      className="card group relative cursor-pointer overflow-hidden"
      onMouseEnter={() => ref.current && ref.current.play().catch(() => {})}
      onMouseLeave={() => ref.current && ref.current.pause()}>
      {item.src ? (
        <video ref={ref} src={item.src} poster={item.poster || undefined} muted loop playsInline preload="metadata"
          className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : <Placeholder icon={<PlayCircle size={28} strokeWidth={1.4} />} label="AI video · hover to play" />}
      {item.src && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 group-hover:opacity-0">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/25" style={{ backdropFilter: "blur(4px)" }}>
            <Play size={24} className="ml-1 text-gold" fill="currentColor" />
          </span>
        </div>
      )}
      <Caption title={item.title} sub="AI Video · click to watch with sound" />
    </div>
  );
};

const EmbedCard = ({ item, onOpen }) => {
  const src = toEmbed(item.url);
  return (
    <div data-testid="ai-item-embed" onClick={src ? onOpen : undefined}
      className={`card relative overflow-hidden ${src ? "cursor-pointer" : ""}`}>
      {src ? (
        <div className="relative h-72 w-full bg-[var(--elevated)]">
          <iframe src={toEmbed(item.url).replace("autoplay=1", "autoplay=0")} title={item.title}
            className="pointer-events-none h-full w-full border-0" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/25" style={{ backdropFilter: "blur(4px)" }}>
              <Play size={24} className="ml-1 text-gold" fill="currentColor" />
            </span>
          </div>
        </div>
      ) : <Placeholder icon={<Clapperboard size={28} strokeWidth={1.4} />} label="AI video · YouTube/Vimeo" />}
      {!src && <Caption title={item.title} sub="AI Video · embed" />}
    </div>
  );
};

const Lightbox = ({ item, onClose }) => {
  const escHandler = useCallback((e) => { if (e.key === "Escape") onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener("keydown", escHandler);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", escHandler); document.body.style.overflow = ""; };
  }, [escHandler]);

  return (
    <div data-testid="ai-lightbox" onClick={onClose}
      className="fixed inset-0 z-[999] flex flex-col items-center justify-center bg-black/90 p-4 sm:p-10"
      style={{ backdropFilter: "blur(10px)", animation: "abcFade 0.25s ease" }}>
      <button data-testid="ai-lightbox-close" onClick={onClose}
        className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-[var(--text)] transition-colors hover:bg-white/20"
        aria-label="Close">
        <X size={22} />
      </button>

      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[85vh] w-full max-w-5xl flex-col items-center">
        {item.type === "video" && (
          <video src={item.src} poster={item.poster || undefined} controls autoPlay loop playsInline
            className="max-h-[80vh] w-auto max-w-full rounded-sm shadow-2xl" />
        )}
        {item.type === "image" && (
          <img src={item.src} alt={item.title} className="max-h-[80vh] w-auto max-w-full rounded-sm shadow-2xl" />
        )}
        {item.type === "embed" && (
          <div className="aspect-video w-full max-w-4xl">
            <iframe src={toEmbed(item.url)} title={item.title} allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen className="h-full w-full rounded-sm border-0" />
          </div>
        )}
        <p className="mt-5 text-center text-sm text-[var(--text)]">
          {item.title}
          <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-gold">{item.caption || (item.type === "image" ? "AI Image" : "AI Video")}</span>
        </p>
      </div>
    </div>
  );
};

export default function AIShowcase() {
  const [active, setActive] = useState(null);
  const hasItems = aiShowcaseItems && aiShowcaseItems.length > 0;
  const openable = (item) => item.type === "embed" ? !!toEmbed(item.url) : !!item.src;

  return (
    <section data-testid="ai-showcase-section" className="border-t border-[var(--border)] px-6 py-32">
      <div className="mx-auto max-w-7xl">
        <Reveal><p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">The AI Studio</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="max-w-3xl font-head text-4xl font-light tracking-tight sm:text-5xl">
            Generative visuals &amp; <span className="text-gold italic">film.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--text-2)]">
            AI-generated images and video, art-directed end to end — from on-brand product shots and campaign visuals to cinematic brand films, animations and AI video ads. Click any piece to view it full-size, with sound. A capability I bring to client work, and a craft I keep pushing.
          </p>
        </Reveal>

        {hasItems ? (
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {aiShowcaseItems.map((item, i) => {
              const onOpen = openable(item) ? () => setActive(item) : undefined;
              return (
                <Reveal key={i} delay={(i % 3) * 0.08}>
                  {item.type === "video" ? <VideoCard item={item} onOpen={onOpen} />
                    : item.type === "embed" ? <EmbedCard item={item} onOpen={onOpen} />
                    : <ImageCard item={item} onOpen={onOpen} />}
                </Reveal>
              );
            })}
          </div>
        ) : (
          <div data-testid="ai-showcase-empty" className="mt-14 border border-[var(--border)] bg-[rgba(15,15,15,0.6)] p-20 text-center" style={{ backdropFilter: "blur(16px)" }}>
            <p className="font-head text-3xl font-light text-[var(--text)]">New Work Rendering</p>
            <p className="mx-auto mt-4 max-w-md text-sm text-[var(--text-2)]">AI images and films appear here as they're finished.</p>
          </div>
        )}
      </div>

      {active && <Lightbox item={active} onClose={() => setActive(null)} />}
    </section>
  );
}
